/**
 * Boot readiness store (spec: docs/designs/boot-readiness.md).
 *
 * An external store — not React context — because its writers live at
 * different depths of the provider tree (PortalSpacetimeProvider is OUTSIDE
 * SpacetimeDBProvider; BootstrapSubscriptions and BootstrappedApp are
 * inside), and the reader (BootOverlay) must not remount when providers do.
 *
 * `ready` is STICKY: once every signal has been observed, the session is
 * booted for good — later disconnects, token refreshes, or gate churn never
 * bring the full-screen preloader (or error card) back.
 */
import { useSyncExternalStore } from "react";

export type BootGate = {
  /** The one-shot inviteParams cookie parse has completed. */
  inviteParsed: boolean;
  /**
   * Signed-out visitor with no invite — held behind the overlay while the
   * login() redirect fires (never reveals).
   */
  anonymousHold: boolean;
};

type BootFlags = {
  tokenResolved: boolean;
  connected: boolean;
  viewsApplied: boolean;
  gate: BootGate;
  ready: boolean;
};

const initialFlags = (): BootFlags => ({
  tokenResolved: false,
  connected: false,
  viewsApplied: false,
  gate: { inviteParsed: false, anonymousHold: false },
  ready: false,
});

let flags = initialFlags();
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function recompute() {
  if (flags.ready) return; // sticky
  if (
    flags.tokenResolved &&
    flags.connected &&
    flags.viewsApplied &&
    flags.gate.inviteParsed &&
    !flags.gate.anonymousHold
  ) {
    flags.ready = true;
    emit();
  }
}

export function markTokenResolved(): void {
  if (flags.tokenResolved) return;
  flags.tokenResolved = true;
  recompute();
}

export function markConnected(): void {
  if (flags.connected) return;
  flags.connected = true;
  recompute();
}

export function markViewsApplied(): void {
  if (flags.viewsApplied) return;
  flags.viewsApplied = true;
  recompute();
}

export function setBootGate(gate: BootGate): void {
  if (
    flags.gate.inviteParsed === gate.inviteParsed &&
    flags.gate.anonymousHold === gate.anonymousHold
  )
    return;
  flags.gate = gate;
  recompute();
}

export function isBootReady(): boolean {
  return flags.ready;
}

export function subscribeBootState(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

/** React subscription to the sticky ready flag. */
export function useBootReady(): boolean {
  return useSyncExternalStore(subscribeBootState, isBootReady, isBootReady);
}

/** Tests only — module state persists across renders by design. */
export function resetBootState(): void {
  flags = initialFlags();
  emit();
}
